/** Token codes, `POLY_*`. A lexed operator travels as its character code. */
const NONE = 0; // Poly.h:15
const ROOT = 1; // Poly.h:16
const MUL = 2; // Poly.h:17
const PLU = 3; // Poly.h:18
const POW = 4; // Poly.h:19
const MIN = 5; // Poly.h:20
const DIV = 6; // Poly.h:21
const NUM = 9; // Poly.h:24
const ID = 10; // Poly.h:25
const EOS = 11; // Poly.h:26
const COS = 12; // Poly.h:27
const SIN = 13; // Poly.h:28
const TAN = 14; // Poly.h:29
const COSEC = 15; // Poly.h:30
const SEC = 16; // Poly.h:32
const COT = 17; // Poly.h:33
const LOG = 18; // Poly.h:36
const LN = 19; // Poly.h:37
const LOG10 = 20; // Poly.h:38
const ABS = 21; // Poly.h:40
const MINF = 22; // Poly.h:41
const MAXF = 23; // Poly.h:42
const IRAND = 24; // Poly.h:43
const FRAND = 25; // Poly.h:44
const MOD = 26; // Poly.h:45
const FLOOR = 27; // Poly.h:46

const PLUS = 43;
const MINUS = 45;
const STAR = 42;
const SLASH = 47;
const PERCENT = 37;
const CARET = 94;
const OPEN = 40;
const CLOSE = 41;
const COMMA = 44;

/** `ERandomType`: how `number(a, b)` resolves. */
export const RANDOM_FREELY = 0; // Poly.h:55
export const RANDOM_FORCE_MIN = 1; // Poly.h:56
export const RANDOM_FORCE_MAX = 2; // Poly.h:57

interface PolySymbol {
  readonly token: number;
  readonly lex: string;
  value: number;
}

const LETTER = /[A-Za-z]/;

/**
 * `CPoly`, the client's formula evaluator: `analyze` compiles the string into
 * postfix tokens by recursive descent, `varNames` lists the identifiers the
 * formula introduced in first-seen order, `setVar` binds one, and `evaluate`
 * walks the tokens and answers a C `float`. A parse error, a division by zero,
 * a function outside its domain, or a formula with no tokens evaluates to 0.
 */
export class Poly {
  private readonly source: string;
  private readonly symbols: PolySymbol[] = [];
  private readonly sorted: number[] = [];
  private readonly mathSymbols: number;
  private tokens: number[] = [];
  private numbers: number[] = [];
  private errored = true; // Poly.cpp:50
  private look = 0;
  private lookAhead = 0;
  private token = 0;
  private numToken = 0;
  private random: number;

  constructor(source: string, random = RANDOM_FREELY) {
    this.source = source;
    this.random = random;
    this.insert("min", MINF); // Poly.cpp:595
    this.insert("max", MAXF); // Poly.cpp:596
    this.insert("number", IRAND); // Poly.cpp:597
    this.insert("irandom", IRAND); // Poly.cpp:598
    this.insert("irand", IRAND); // Poly.cpp:599
    this.insert("frandom", FRAND); // Poly.cpp:600
    this.insert("frand", FRAND); // Poly.cpp:601
    this.insert("rt", ROOT); // Poly.cpp:602
    this.insert("sqrt", ROOT); // Poly.cpp:603
    this.insert("cos", COS); // Poly.cpp:604
    this.insert("sin", SIN); // Poly.cpp:605
    this.insert("tan", TAN); // Poly.cpp:606
    this.insert("cot", COT); // Poly.cpp:607
    this.insert("csc", COSEC); // Poly.cpp:608
    this.insert("cosec", COSEC); // Poly.cpp:609
    this.insert("sec", SEC); // Poly.cpp:610
    this.insert("pi", ID).value = Math.PI; // Poly.cpp:611-612
    this.insert("e", ID).value = Math.E; // Poly.cpp:613-614
    this.insert("log", LOG); // Poly.cpp:615
    this.insert("ln", LN); // Poly.cpp:616
    this.insert("log10", LOG10); // Poly.cpp:617
    this.insert("abs", ABS); // Poly.cpp:618
    this.insert("mod", MOD); // Poly.cpp:619
    this.insert("floor", FLOOR); // Poly.cpp:620
    this.mathSymbols = this.symbols.length; // Poly.cpp:621
  }

  /** `SetRandom`: how the next `evaluate` resolves `number`. */
  setRandom(random: number): void {
    this.random = random;
  }

  /** `Analyze`: whether the string compiled; an empty string compiles to nothing. */
  analyze(): boolean {
    if (this.source.length === 0) return true; // Poly.cpp:239
    this.errored = false;
    this.look = 0;
    this.lookAhead = this.lexan();
    this.expr();
    if (this.tokens.length === 0) return false; // Poly.cpp:249
    return !this.errored; // Poly.cpp:255
  }

  /** `GetVarCount`/`GetVarName`: the identifiers past the built-in names. */
  varNames(): string[] {
    return this.symbols.slice(this.mathSymbols).map((symbol) => symbol.lex); // Poly.cpp:584
  }

  /** `SetVar`: binds a name the formula introduced; refused after an error. */
  setVar(name: string, value: number): boolean {
    if (this.errored) return false; // Poly.cpp:574
    const index = this.find(name);
    if (index === -1) return false;
    this.symbols[index].value = value; // Poly.cpp:578
    return true;
  }

  /** `Eval`: the compiled tokens on a value stack. */
  evaluate(): number {
    if (this.errored) return 0; // Poly.cpp:73
    const save: number[] = [];
    let sp = 0;
    let n = 0;
    let t = 0;
    for (let pos = 0; pos < this.tokens.length; ) {
      switch (this.tokens[pos++]) {
        case NUM:
          save[sp++] = this.numbers[n++];
          break;
        case ID:
          save[sp++] = this.symbols[this.tokens[pos++]].value;
          break;
        case PLU:
          sp--;
          save[sp - 1] = save[sp - 1] + save[sp];
          break;
        case MIN:
          sp--;
          save[sp - 1] = save[sp - 1] - save[sp];
          break;
        case MUL:
          sp--;
          save[sp - 1] = save[sp - 1] * save[sp];
          break;
        case MOD:
          sp--;
          if (save[sp] === 0) return 0; // Poly.cpp:113
          save[sp - 1] = save[sp - 1] % save[sp];
          break;
        case DIV:
          sp--;
          if (save[sp] === 0) return 0; // Poly.cpp:122
          save[sp - 1] = save[sp - 1] / save[sp];
          break;
        case POW:
          sp--;
          save[sp - 1] = Math.pow(save[sp - 1], save[sp]);
          break;
        case ROOT:
          if (save[sp - 1] < 0) return 0; // Poly.cpp:133
          save[sp - 1] = Math.sqrt(save[sp - 1]);
          break;
        case COS:
          save[sp - 1] = Math.cos(save[sp - 1]);
          break;
        case SIN:
          save[sp - 1] = Math.sin(save[sp - 1]);
          break;
        case TAN:
          if (!Math.cos(save[sp - 1])) return 0; // Poly.cpp:144
          save[sp - 1] = Math.tan(save[sp - 1]);
          break;
        case COSEC:
          if (!(t = Math.sin(save[sp - 1]))) return 0; // Poly.cpp:151
          save[sp - 1] = 1 / t;
          break;
        case SEC:
          if (!(t = Math.cos(save[sp - 1]))) return 0; // Poly.cpp:158
          save[sp - 1] = 1 / t;
          break;
        case COT:
          if (!(t = Math.sin(save[sp - 1]))) return 0; // Poly.cpp:165
          save[sp - 1] = Math.cos(save[sp - 1]) / t;
          break;
        case LN:
          if (save[sp - 1] <= 0) return 0; // Poly.cpp:172
          save[sp - 1] = Math.log(save[sp - 1]);
          break;
        case LOG10:
          if (save[sp - 1] <= 0) return 0; // Poly.cpp:179
          save[sp - 1] = Math.log10(save[sp - 1]);
          break;
        case LOG:
          if (save[sp - 1] <= 0) return 0; // Poly.cpp:186
          if (save[sp - 2] <= 0 || save[sp - 2] === 1) return 0; // Poly.cpp:191
          save[sp - 2] = Math.log(save[sp - 1]) / Math.log(save[sp - 2]);
          sp--;
          break;
        case ABS:
          save[sp - 1] = Math.abs(save[sp - 1]);
          break;
        case FLOOR:
          save[sp - 1] = Math.floor(save[sp - 1]);
          break;
        case IRAND:
          save[sp - 2] = this.irandom(save[sp - 2], save[sp - 1]);
          sp--;
          break;
        case FRAND:
          save[sp - 2] = Math.random() * (save[sp - 1] - save[sp - 2]) + save[sp - 2]; // Poly.cpp:43
          sp--;
          break;
        case MINF:
          save[sp - 2] = save[sp - 2] < save[sp - 1] ? save[sp - 2] : save[sp - 1];
          sp--;
          break;
        case MAXF:
          save[sp - 2] = save[sp - 2] > save[sp - 1] ? save[sp - 2] : save[sp - 1];
          sp--;
          break;
        default:
          return 0; // Poly.cpp:226
      }
    }
    return sp > 0 ? Math.fround(save[sp - 1]) : 0; // Poly.cpp:231
  }

  private irandom(start: number, end: number): number {
    if (this.random === RANDOM_FORCE_MIN) return Math.trunc(start); // Poly.cpp:26
    if (this.random === RANDOM_FORCE_MAX) return Math.trunc(end); // Poly.cpp:29
    const is = Math.trunc(start + 0.5); // Poly.cpp:35
    const ie = Math.trunc(end - start + 0.5) + 1; // Poly.cpp:36
    return Math.trunc(Math.random() * ie + is); // Poly.cpp:38
  }

  private expr(): void {
    if (this.lookAhead === PLUS || this.lookAhead === MINUS) {
      this.look--; // Poly.cpp:286
      this.lookAhead = NUM;
      this.numToken = this.token = 0;
    }
    this.term();
    while (!this.errored) {
      const t = this.lookAhead;
      if (t === PLUS || t === MINUS) {
        this.match(t);
        this.term();
        this.emit(t, NONE);
        continue;
      }
      if (t === EOS || t === CLOSE || t === COMMA) return; // Poly.cpp:305
      this.errored = true; // Poly.cpp:307
      return;
    }
  }

  private lexan(): number {
    const data = this.source;
    while (this.look < data.length) {
      const c = data[this.look];
      if (c === " " || c === "\t") {
        this.look++;
        continue;
      }
      if (c >= "0" && c <= "9") {
        let t = 0;
        for (; this.look < data.length; this.look++) {
          const d = data[this.look];
          if (d >= "0" && d <= "9") t = t * 10 + (d.charCodeAt(0) - 48);
          else break;
        }
        this.token = t;
        let tt = 0.1;
        this.numToken = 0;
        if (this.look < data.length && data[this.look] === ".") {
          this.look++;
          for (; this.look < data.length; this.look++, tt *= 0.1) {
            const d = data[this.look];
            if (d >= "0" && d <= "9") this.numToken += tt * (d.charCodeAt(0) - 48); // Poly.cpp:348
            else break;
          }
        }
        this.numToken += this.token;
        return NUM;
      }
      if (LETTER.test(c)) {
        let lex = "";
        while (this.look < data.length && LETTER.test(data[this.look])) {
          lex += data[this.look];
          this.look++;
        }
        let index = this.find(lex);
        if (index === -1) {
          this.insert(lex, ID);
          index = this.symbols.length - 1;
        }
        this.token = index;
        return this.symbols[index].token;
      }
      this.token = 0;
      this.look++;
      return c.charCodeAt(0);
    }
    return EOS;
  }

  private term(): void {
    this.factor();
    while (!this.errored) {
      const t = this.lookAhead;
      if (t !== STAR && t !== SLASH && t !== PERCENT) return;
      this.match(t);
      this.factor();
      this.emit(t, NONE);
    }
  }

  private factor(): void {
    this.expo();
    while (!this.errored && this.lookAhead === CARET) {
      this.match(CARET);
      this.expo();
      this.emit(CARET, NONE);
    }
  }

  private expo(): void {
    const t = this.lookAhead;
    switch (t) {
      case OPEN:
        this.match(OPEN);
        this.expr();
        this.match(CLOSE);
        return;
      case NUM:
        this.emit(NUM, this.token);
        this.match(NUM);
        return;
      case ID:
        this.emit(ID, this.token);
        this.match(ID);
        return;
      case ROOT:
      case SIN:
      case COT:
      case TAN:
      case COSEC:
      case SEC:
      case LN:
      case LOG10:
      case COS:
      case ABS:
      case FLOOR:
        this.match(t);
        this.match(OPEN);
        this.expr();
        this.match(CLOSE);
        this.emit(t, this.token);
        return;
      case LOG:
      case MINF:
      case MAXF:
      case IRAND:
      case FRAND:
      case MOD:
        this.match(t);
        this.match(OPEN);
        this.expr();
        this.match(COMMA);
        this.expr();
        this.match(CLOSE);
        this.emit(t, this.token);
        return;
      case EOS:
        return;
      default:
        this.errored = true; // Poly.cpp:460
    }
  }

  private match(t: number): void {
    if (this.lookAhead === t) this.lookAhead = this.lexan();
    else this.errored = true; // Poly.cpp:467
  }

  private emit(t: number, value: number): void {
    switch (t) {
      case PLUS:
        this.tokens.push(PLU);
        return;
      case MINUS:
        this.tokens.push(MIN);
        return;
      case STAR:
        this.tokens.push(MUL);
        return;
      case SLASH:
        this.tokens.push(DIV);
        return;
      case PERCENT:
        this.tokens.push(MOD);
        return;
      case CARET:
        this.tokens.push(POW);
        return;
      case ROOT:
      case SIN:
      case TAN:
      case COT:
      case COS:
      case COSEC:
      case SEC:
      case LOG:
      case LN:
      case LOG10:
      case ABS:
      case MINF:
      case MAXF:
      case IRAND:
      case FRAND:
      case MOD:
      case FLOOR:
        this.tokens.push(t);
        return;
      case NUM:
        this.tokens.push(t);
        this.numbers.push(this.numToken);
        return;
      case ID:
        this.tokens.push(t, value);
        return;
      default:
        this.errored = true; // Poly.cpp:519
        this.tokens = []; // Poly.cpp:520
        this.numbers = [];
    }
  }

  private find(lex: string): number {
    let l = 0;
    let r = this.sorted.length - 1;
    while (l <= r) {
      const m = (l + r) >> 1;
      const at = this.symbols[this.sorted[m]].lex;
      if (at === lex) return this.sorted[m];
      if (at < lex) l = m + 1;
      else r = m - 1;
    }
    return -1;
  }

  private insert(lex: string, token: number): PolySymbol {
    const symbol: PolySymbol = { token, lex, value: 0 }; // SymTable.cpp:6
    this.symbols.push(symbol);
    const at = this.sorted.findIndex((index) => lex < this.symbols[index].lex);
    if (at === -1) this.sorted.push(this.symbols.length - 1); // Poly.cpp:565
    else this.sorted.splice(at, 0, this.symbols.length - 1); // Poly.cpp:557
    return symbol;
  }
}
